import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { io as socketIo } from 'socket.io-client';
import {
  Box,
  Paper,
  Typography,
  LinearProgress,
  Chip,
  Stack,
  IconButton,
  Collapse,
  Tooltip,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CloseIcon from '@mui/icons-material/Close';
import MinimizeIcon from '@mui/icons-material/Minimize';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || '';

const statusLabel = {
  queued: 'Queued',
  running: 'Running',
  completed: 'Completed',
  failed: 'Failed',
};

const TOAST_DURATION_MS = 8000;

export default function SchedulingProgressWidget() {
  const { user } = useAuth();
  const socketRef = useRef(null);
  const [jobs, setJobs] = useState([]);
  const [toasts, setToasts] = useState([]); // { id, status, message, title }
  const [minimized, setMinimized] = useState(false);
  const toastTimersRef = useRef({});

  const dismissToast = useCallback((id) => {
    clearTimeout(toastTimersRef.current[id]);
    delete toastTimersRef.current[id];
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const addToast = useCallback((job) => {
    const toast = {
      id: job.id,
      status: job.status,
      title: job.type === 'traffic' ? 'Advertising' : 'Clocks',
      message: job.message || (job.status === 'completed' ? 'Generation complete.' : 'Generation failed.'),
    };
    setToasts((prev) => {
      const filtered = prev.filter((t) => t.id !== toast.id);
      return [...filtered, toast];
    });
    // Auto-dismiss completed toasts; keep failed ones until manually closed
    if (job.status === 'completed') {
      toastTimersRef.current[toast.id] = setTimeout(() => dismissToast(toast.id), TOAST_DURATION_MS);
    }
  }, [dismissToast]);

  const upsertJob = useCallback((incoming) => {
    if (!incoming?.job_id && !incoming?.id) return;

    const normalized = {
      id: incoming.job_id || incoming.id,
      type: incoming.type || 'playlist',
      title: incoming.title || '',
      status: incoming.status || (incoming.done ? (incoming.error ? 'failed' : 'completed') : 'running'),
      percent: typeof incoming.percent === 'number' ? incoming.percent : 0,
      message: incoming.message || '',
      queue_position: incoming.queue_position ?? null,
      requested_by: incoming.requested_by ?? null,
      updated_at: new Date().toISOString(),
    };

    if (user?.id && normalized.requested_by && normalized.requested_by !== user.id) {
      return;
    }

    setJobs((prev) => {
      const filtered = prev.filter((job) => job.id !== normalized.id);
      if (normalized.status === 'completed' || normalized.status === 'failed') {
        addToast(normalized);
        return filtered;
      }
      return [...filtered, normalized];
    });
  }, [user?.id, addToast]);

  const fetchActiveJobs = useCallback(async () => {
    if (!user) return;
    try {
      const res = await axios.get(`${API_URL}/api/scheduling-jobs/active`, { withCredentials: true });
      const list = (res.data?.jobs || []).map((job) => ({
        id: job.id,
        type: job.type,
        title: job.title,
        status: job.status,
        percent: typeof job.percent === 'number' ? job.percent : 0,
        message: job.message || '',
        queue_position: job.queue_position ?? null,
        requested_by: job.requested_by ?? null,
        updated_at: job.updated_at,
      }));
      setJobs(list);
    } catch (_) {
    }
  }, [user]);

  useEffect(() => {
    fetchActiveJobs();
    const interval = setInterval(fetchActiveJobs, 7000);
    return () => clearInterval(interval);
  }, [fetchActiveJobs]);

  useEffect(() => {
    const socket = socketIo(API_URL);
    socketRef.current = socket;

    socket.on('generation:progress', (event) => {
      if (!event?.job_id) return;
      upsertJob(event);
    });

    return () => {
      socket.close();
      socketRef.current = null;
    };
  }, [user?.id, upsertJob]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      Object.values(toastTimersRef.current).forEach(clearTimeout);
    };
  }, []);

  const visibleJobs = useMemo(
    () => [...jobs].sort((a, b) => {
      if (a.status === 'running' && b.status !== 'running') return -1;
      if (a.status !== 'running' && b.status === 'running') return 1;
      return (a.queue_position || 999) - (b.queue_position || 999);
    }),
    [jobs]
  );

  const runningCount = useMemo(
    () => visibleJobs.filter((job) => job.status === 'running').length,
    [visibleJobs]
  );
  const queuedCount = useMemo(
    () => visibleJobs.filter((job) => job.status === 'queued').length,
    [visibleJobs]
  );

  const summaryText = useMemo(() => {
    const parts = [];
    if (runningCount) parts.push(`${runningCount} running`);
    if (queuedCount) parts.push(`${queuedCount} queued`);
    return parts.join(', ') || 'No active jobs';
  }, [runningCount, queuedCount]);

  if (!visibleJobs.length && !toasts.length) return null;

  return (
    <Paper
      elevation={6}
      sx={{
        position: 'fixed',
        right: 20,
        bottom: 20,
        width: 380,
        maxWidth: 'calc(var(--rs-vw) - 24px)',
        p: 2,
        zIndex: (theme) => theme.zIndex.snackbar + 2,
      }}
    >
      {visibleJobs.length > 0 && (
        <>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: minimized ? 0 : 1.5,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Scheduling In Progress
              </Typography>
              {minimized && (
                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                  {summaryText}
                </Typography>
              )}
            </Box>
            <Tooltip title={minimized ? 'Expand' : 'Minimise'}>
              <IconButton
                size="small"
                onClick={() => setMinimized((prev) => !prev)}
                sx={{ ml: 1, flexShrink: 0 }}
                aria-label={minimized ? 'Expand scheduling progress' : 'Minimise scheduling progress'}
              >
                {minimized ? <OpenInFullIcon fontSize="small" /> : <MinimizeIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Box>
          {minimized && (
            <LinearProgress
              variant={runningCount ? 'indeterminate' : 'determinate'}
              value={runningCount ? undefined : 0}
              sx={{ mt: 1, mb: toasts.length ? 2 : 0 }}
            />
          )}
          <Collapse in={!minimized} timeout="auto" unmountOnExit>
            <Stack spacing={1.5} sx={{ mb: toasts.length ? 2 : 0, maxHeight: 'calc(var(--rs-vh) * 0.6)', overflowY: 'auto' }}>
              {visibleJobs.map((job) => (
                <Box key={job.id}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {job.type === 'traffic' ? 'Advertising' : 'Clocks'}
                    </Typography>
                    <Chip
                      size="small"
                      label={statusLabel[job.status] || 'Running'}
                      color={job.status === 'queued' ? 'warning' : 'info'}
                      variant="outlined"
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                    {job.status === 'queued' && job.queue_position
                      ? `Queued (#${job.queue_position})`
                      : (job.message || 'Processing...')}
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={job.status === 'queued' ? 0 : Math.max(0, Math.min(100, job.percent || 0))}
                  />
                </Box>
              ))}
            </Stack>
          </Collapse>
        </>
      )}

      {toasts.length > 0 && (
        <Stack spacing={1}>
          {toasts.map((toast) => (
            <Box
              key={toast.id}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                p: 1,
                borderRadius: 1,
                bgcolor: toast.status === 'failed'
                  ? 'rgba(244,67,54,0.12)'
                  : 'rgba(76,175,80,0.12)',
                border: '1px solid',
                borderColor: toast.status === 'failed' ? 'error.dark' : 'success.dark',
              }}
            >
              {toast.status === 'failed'
                ? <ErrorOutlineIcon fontSize="small" sx={{ color: 'error.main', mt: 0.25, flexShrink: 0 }} />
                : <CheckCircleOutlineIcon fontSize="small" sx={{ color: 'success.main', mt: 0.25, flexShrink: 0 }} />}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: toast.status === 'failed' ? 'error.light' : 'success.light' }}>
                  {toast.title} — {toast.status === 'failed' ? 'Failed' : 'Done'}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', wordBreak: 'break-word' }}>
                  {toast.message}
                </Typography>
              </Box>
              <IconButton size="small" onClick={() => dismissToast(toast.id)} sx={{ mt: -0.5, flexShrink: 0 }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Stack>
      )}
    </Paper>
  );
}
